import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faKey } from '@fortawesome/free-solid-svg-icons';

import { backgroundGrey, grape } from 'lib/css/colors';

function CopyTextModal({ show, onHide, title, text, icon = faKey }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  // Reset copied state when text changes or modal closes
  useEffect(() => {
    setCopied(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [text, show]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // eslint-disable-next-line space-before-function-paren
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 3000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleClose = () => {
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="sm">
      <Modal.Body className={css(styles.modalBody)}>
        <div className={css(styles.iconContainer)}>
          <FontAwesomeIcon icon={icon} className={css(styles.titleIcon)} />
        </div>
        <h5 className={css(styles.title)}>{title}</h5>
        <div className={css(styles.textContainer)}>
          <code className={css(styles.codeText)}>{text || 'Not available'}</code>
          <Button
            variant={copied ? 'success' : 'primary'}
            className={css(styles.copyButton)}
            onClick={handleCopy}
            disabled={!text}
            size="sm"
          >
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="mr-1" />
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
        </div>
        <Button variant="link" className={css(styles.closeLink)} onClick={handleClose}>
          Close
        </Button>
      </Modal.Body>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalBody: {
    padding: '1.5rem',
    textAlign: 'center',
  },
  iconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: backgroundGrey,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1rem',
  },
  titleIcon: {
    fontSize: '1.25rem',
    color: grape,
  },
  title: {
    fontWeight: 600,
    marginBottom: '1rem',
    color: '#333',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
  },
  codeText: {
    display: 'block',
    width: '100%',
    padding: '0.75rem 1rem',
    backgroundColor: backgroundGrey,
    borderRadius: '8px',
    fontSize: '0.8rem',
    color: '#333',
    wordBreak: 'break-all',
    textAlign: 'center',
  },
  copyButton: {
    borderRadius: '6px',
    padding: '0.5rem 1.25rem',
    fontWeight: 500,
    backgroundColor: grape,
    borderColor: grape,
    ':hover': {
      backgroundColor: '#8a7ae6',
      borderColor: '#8a7ae6',
    },
  },
  closeLink: {
    marginTop: '0.75rem',
    color: '#666',
    textDecoration: 'none',
    fontSize: '0.875rem',
    ':hover': {
      color: '#333',
    },
  },
});

CopyTextModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string,
  icon: PropTypes.object,
};

export default CopyTextModal;
